import { createFileRoute, useCanGoBack, useRouter } from '@tanstack/react-router';
import { ArrowLeft, ReceiptText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  EmptyState,
  IconButton,
  SegmentedControl,
  Skeleton,
  TopAppBar,
} from '@nokowazzi/ui';
import { requireAuthBeforeLoad } from '../features/auth/guard';
import { useOrdersQuery, type OrderRole } from '../features/orders/api';
import { OrderCard } from '../features/orders/OrderCard';

export const Route = createFileRoute('/orders/')({
  beforeLoad: () => requireAuthBeforeLoad('/orders'),
  component: OrdersListScreen,
});

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-3 px-5 pt-2" aria-busy="true">
      {[0, 1, 2].map((key) => (
        <Skeleton key={key} className="h-40 w-full rounded-lg" />
      ))}
    </div>
  );
}

function OrdersListScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = Route.useNavigate();

  const [role, setRole] = useState<OrderRole>('buyer');
  const ordersQuery = useOrdersQuery({ role });

  const handleBack = () => {
    if (canGoBack) {
      router.history.back();
    } else {
      void navigate({ to: '/my' });
    }
  };

  const goToOrder = (orderId: string) => {
    void navigate({ to: '/orders/$orderId', params: { orderId } });
  };

  const items = ordersQuery.data?.items ?? [];

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <TopAppBar
        title={t('escrow.orders.title')}
        leading={
          <IconButton aria-label={t('actions.back')} onClick={handleBack}>
            <ArrowLeft aria-hidden="true" />
          </IconButton>
        }
      />

      <div className="px-5 pt-1 pb-3">
        <SegmentedControl<OrderRole>
          options={[
            { value: 'buyer', label: t('escrow.orders.roleBuyer') },
            { value: 'seller', label: t('escrow.orders.roleSeller') },
          ]}
          value={role}
          onChange={setRole}
          aria-label={t('escrow.orders.title')}
        />
      </div>

      {ordersQuery.isPending ? (
        <ListSkeleton />
      ) : ordersQuery.isError ? (
        <EmptyState
          className="mt-12"
          icon={<ReceiptText size={48} strokeWidth={1.5} />}
          title={t('errors.generic')}
          action={
            <Button variant="tertiary" onClick={() => void ordersQuery.refetch()}>
              {t('actions.retry')}
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          className="mt-12"
          icon={<ReceiptText size={48} strokeWidth={1.5} />}
          title={t('escrow.orders.empty')}
          description={
            role === 'buyer'
              ? t('escrow.orders.emptyBuyerDescription')
              : t('escrow.orders.emptySellerDescription')
          }
        />
      ) : (
        <main className="flex flex-col gap-3 px-5 pb-[calc(var(--safe-bottom)+24px)]">
          {items.map((item) => (
            <OrderCard key={item.order.id} item={item} role={role} onClick={goToOrder} />
          ))}
        </main>
      )}
    </div>
  );
}
